import React, { useEffect, useState } from "react";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Typography,
} from "@mui/material";
import { FilterValue } from "../../../../models/Filters/FilterValue";

type AppSingleSearchDropdownProps = {
  required?: boolean;
  title?: string;
  value?: FilterValue | null;
  list: FilterValue[];
  local?: boolean;
  fontSize?: string;
  handleSearch?: (searchText?: string) => void;
  handleSelect?: (selectedItem: FilterValue | null) => void;
  disabled?: boolean;
  minWidth?: string;
  borderColor?: string;
  error?: boolean;
  errorMessage?: string;
  showBorder?: boolean;
  backgroundColor?: string;
  inputHeight?: string;
};

export default function AppSingleSearchDropdown({
  required = false,
  title,
  list,
  value,
  local = false,
  fontSize,
  handleSearch,
  handleSelect,
  disabled = false,
  minWidth,
  borderColor = "#ddd",
  error = false,
  errorMessage = "This is a required field",
  showBorder = true,
  backgroundColor,
  inputHeight = "auto",
}: AppSingleSearchDropdownProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<readonly FilterValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!local) {
      setOptions([...list]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, local]);

  const handleOpen = () => {
    if (!disabled) {
      setOpen(true);
      setOptions([...list]);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value.trim();
    setSearchText(searchValue);

    if (!open) return;

    if (local) {
      const filteredValues = list.filter((item) =>
        new RegExp(searchValue, "i").test(item.value)
      );
      setOptions(filteredValues);
    } else {
      setLoading(true);
      handleSearch?.(searchValue);
    }
  };

  useEffect(() => {
    if (value) {
      setSearchText(value.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <>
      <Autocomplete
        fullWidth
        sx={{
          minWidth: minWidth,
          fontSize: fontSize,
          height: inputHeight,
          "& .MuiOutlinedInput-root": {
            padding: "0px !important",
            paddingRight: "45px !important",
            borderColor: showBorder
              ? `${error ? "#d32f2f" : borderColor}`
              : "none",
            borderWidth: showBorder ? "1px" : "0px",
            backgroundColor: disabled ? "#f5f5f5" : backgroundColor,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: showBorder
                ? error
                  ? "#d32f2f"
                  : borderColor
                : "transparent",
            },
            "&:hover fieldset": {
              borderColor: showBorder
                ? error
                  ? "#d32f2f"
                  : "#6455ED"
                : "transparent",
            },
            "&.Mui-focused fieldset": {
              borderColor: showBorder
                ? error
                  ? "#d32f2f"
                  : "#6455ED"
                : "transparent",
            },
          },
          "& .MuiInputLabel-root": {
            color: error ? "#d32f2f" : "#93969B",
          },
          "& .MuiInputLabel-root.Mui-focused": {
            color: error ? "#d32f2f" : "#6455ED",
          },
          "& .MuiInputBase-root": {
            height: inputHeight,
          },
          "& .MuiAutocomplete-clearIndicator": {
            width: "8px",
            height: "8px",
            right: "1rem",
            fontSize: "1rem !important",
          },
          "& .MuiSvgIcon-root": {
            fontSize: "16px",
            color: "#888",
            marginRight: "0px",
          },
          "& .MuiAutocomplete-popupIndicator": {
            width: "6px",
            height: "6px",
            fontSize: "1.5rem !important",
          },
        }}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        isOptionEqualToValue={(option, val) => option.id === val?.id}
        getOptionLabel={(option) => option.value}
        options={options}
        loading={loading}
        noOptionsText={<Typography sx={{ fontSize }}>No options</Typography>}
        loadingText={<Typography sx={{ fontSize }}>Loading...</Typography>}
        inputValue={searchText}
        value={value || null}
        disabled={disabled}
        onChange={(event, selectedValue: FilterValue | null) => {
          handleSelect?.(selectedValue);
          setSearchText(selectedValue ? selectedValue.value : "");
          setLoading(false);
          if (selectedValue) {
            setTimeout(() => {
              const input = document.querySelector(
                ".MuiAutocomplete-input"
              ) as HTMLInputElement;
              input?.blur();
            }, 100);
          }
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "input") {
            setSearchText(newInputValue);
            setLoading(true);
          }
          if (newInputValue === "") {
            setLoading(false);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={title}
            required={required}
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onFocus={(e) => {
              window.requestAnimationFrame(() => {
                const length = e.target.value.length;
                e.target.setSelectionRange(length, length);
              });
            }}
            onChange={handleInputChange}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={10} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
                style: {
                  fontSize: fontSize,
                },
              },
            }}
          />
        )}
      />
      {error && (
        <Typography
          sx={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}
        >
          {errorMessage}
        </Typography>
      )}
    </>
  );
}
